import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Star, X, FloppyDisk, CircleNotch, Receipt } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import InvoiceLookTab from "@/components/invoice/InvoiceLookTab";
import TouristTaxTab from "@/components/invoice/TouristTaxTab";
import PropertyNumbering, { NumberingHelpButton } from "@/components/invoice/PropertyNumbering";

const EMPTY = {
  name: "",
  issuer_type: "private",
  private_landlord_name: "",
  company_name: "",
  oib: "",
  vat_id: "",
  address: "",
  city: "",
  postal_code: "",
  country: "HR",
  iban: "",
  swift: "",
  bank_name: "",
  email: "",
  phone: "",
  website: "",
  logo_url: "",
  payment_model: "HR99",
  reference_template: "{broj}-{uredaj}-{godina}",
  footer_text: "",
  vat_registered: false,
  vat_rate: 25.0,
  pdv_exemption_text: "PDV nije obračunat temeljem čl. 90 stavak 2, Zakona o PDV-u",
  tourist_tax_text: "Boravišna pristojba je obračunata i plaćena u paušalnom iznosu",
  issued_by_name: "",
  is_default: false,
};

/**
 * Postavke → Fakturiranje
 * Manages tenant-scoped `invoice_profiles`. Each profile carries the legal
 * details that appear on issued invoices. Properties later reference these
 * profiles (see PropertyEdit → dropdown) so a single tenant with several
 * paušal/d.o.o. identities can bill correctly per-object.
 */
export default function InvoicingSettingsPage() {
  const [profiles, setProfiles] = useState(null);
  const [properties, setProperties] = useState([]);
  const [editing, setEditing] = useState(null); // profile id or "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("izdavatelji");

  const load = async () => {
    try {
      const [pr, ps] = await Promise.all([
        api.get("/invoice-profiles"),
        api.get("/properties"),
      ]);
      setProfiles(pr.data || []);
      setProperties(ps.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const assignPropertyProfile = async (propertyId, profileId) => {
    try {
      await api.put(`/properties/${propertyId}`, { invoice_profile_id: profileId || null });
      toast.success("Objekt povezan s profilom.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const assignPropertyDisplayName = async (propertyId, displayName) => {
    try {
      await api.put(`/properties/${propertyId}`, { invoice_display_name: displayName || null });
      toast.success("Trgovački naziv za račun spremljen.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e?.preventDefault?.();
    const isCompany = form.issuer_type === "company";
    if (!(form.name || "").trim() || !((isCompany ? form.company_name : form.private_landlord_name) || "").trim()) {
      toast.error(isCompany ? "Naziv profila i naziv tvrtke su obavezni." : "Naziv profila i ime iznajmljivača su obavezni.");
      return;
    }
    if (!(form.pdv_exemption_text || "").trim()) {
      toast.error("Tekst o PDV-u je obavezan.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/invoice-profiles", form);
        toast.success("Profil kreiran.");
      } else {
        await api.patch(`/invoice-profiles/${editing}`, form);
        toast.success("Profil ažuriran.");
      }
      close();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (id) => {
    try {
      await api.patch(`/invoice-profiles/${id}`, { is_default: true });
      toast.success("Postavljeno kao zadano.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Obrisati ovaj profil?")) return;
    try {
      await api.delete(`/invoice-profiles/${id}`);
      toast.success("Profil obrisan.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (profiles === null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <CircleNotch size={22} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <section className="space-y-6" data-testid="invoicing-settings-page">
      <div className="flex gap-1 border-b border-border">
        {[["izdavatelji", "Izdavatelji i objekti"], ["izgled", "Izgled računa"], ["pristojba", "Boravišna pristojba"]].map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium ${tab === k ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`} data-testid={`invoicing-tab-${k}`}>{v}</button>
        ))}
      </div>
      {tab === "izgled" && <InvoiceLookTab profiles={profiles} />}
      {tab === "pristojba" && <TouristTaxTab />}
      {tab === "izdavatelji" && (<>
      <div className="flex items-start justify-between gap-3">
        <h1 className="hidden font-heading text-2xl font-bold tracking-tight text-foreground sm:block">Podaci za račune</h1>
        <button
          onClick={openNew}
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          data-testid="invoicing-new-btn"
        >
          <Plus size={14} weight="bold" /> Novi profil
        </button>
      </div>

      {profiles.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-10 text-center">
          <Receipt size={28} weight="duotone" className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">Još nemate niti jedan profil za račune.</p>
          <p className="mt-1 text-xs text-muted-foreground">Kreirajte prvi profil kako biste mogli izdavati fiskalne račune.</p>
          <button
            onClick={openNew}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            data-testid="invoicing-empty-new-btn"
          >
            <Plus size={14} weight="bold" /> Kreiraj prvi profil
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {profiles.map((p) => (
            <div key={p.id} className="rounded-lg border border-border bg-card p-4" data-testid={`profile-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-sm font-semibold text-foreground">{p.name}</h3>
                    {p.is_default && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                        <Star size={10} weight="fill" /> Zadano
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{p.company_name || p.private_landlord_name}</p>
                </div>
                <div className="flex items-center gap-1">
                  {!p.is_default && (
                    <button
                      onClick={() => setDefault(p.id)}
                      title="Postavi kao zadano"
                      className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-primary"
                      data-testid={`profile-default-${p.id}`}
                    >
                      <Star size={14} />
                    </button>
                  )}
                  <button onClick={() => openEdit(p)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" title="Uredi" data-testid={`profile-edit-${p.id}`}>
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => remove(p.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" title="Obriši" data-testid={`profile-delete-${p.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                {p.oib && (<><dt className="text-muted-foreground">OIB</dt><dd className="mono text-foreground">{p.oib}</dd></>)}
                {p.address && (<><dt className="text-muted-foreground">Adresa</dt><dd className="text-foreground">{p.address}{p.city ? `, ${p.city}` : ""}</dd></>)}
                {p.iban && (<><dt className="text-muted-foreground">IBAN</dt><dd className="mono truncate text-foreground">{p.iban}</dd></>)}
                <dt className="text-muted-foreground">U sustavu PDV-a</dt>
                <dd className="text-foreground">{p.vat_registered ? `Da (${p.vat_rate}%)` : "Ne"}</dd>
              </dl>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProfileEditor
          form={form}
          setForm={setForm}
          onSave={save}
          onCancel={close}
          saving={saving}
          isNew={editing === "new"}
        />
      )}

      {/* Objects → profiles mapping */}
      {profiles.length > 0 && properties.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4" data-testid="property-profile-mapping">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-foreground">Objekti, profili i numeracija računa</h3>
            <NumberingHelpButton />
          </div>

          {/* Column headers (desktop only) */}
          <div className="mt-4 hidden items-center gap-4 border-b border-border pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:flex">
            <span className="flex-1 basis-1/3">Objekt (Property)</span>
            <span className="flex-1 basis-1/3">Profil za račune (vlasnik)</span>
            <span className="flex-1 basis-1/3">Trgovački naziv na računu</span>
          </div>

          <div className="mt-2 space-y-2">
            {properties.map((p) => {
              const currentProfile = profiles.find((pr) => pr.id === p.invoice_profile_id);
              const defaultProfile = profiles.find((pr) => pr.is_default);
              const effectiveProfile = currentProfile || defaultProfile;
              return (
                <div key={p.id} className="rounded-md border border-border bg-background px-3 py-3" data-testid={`property-mapping-row-${p.id}`}>
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
                    {/* Objekt info — više neće biti prekrit dropdownom */}
                    <div className="min-w-0 flex-1 basis-1/3">
                      <p className="truncate text-sm font-semibold text-foreground" title={p.name}>
                        {p.name || <em className="text-muted-foreground">Bez naziva</em>}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                        {[p.city, p.country].filter(Boolean).join(", ") || "—"}
                        {p.type && <span className="ml-1 rounded bg-muted px-1.5 py-0.5">{p.type}</span>}
                      </p>
                    </div>
                    {/* Profile picker */}
                    <div className="min-w-0 flex-1 basis-1/3">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Profil za račune</label>
                      <select
                        value={p.invoice_profile_id || ""}
                        onChange={(e) => assignPropertyProfile(p.id, e.target.value)}
                        className="rw-input w-full"
                        data-testid={`assign-profile-${p.id}`}
                      >
                        <option value="">— zadani profil —</option>
                        {profiles.map((prof) => (
                          <option key={prof.id} value={prof.id}>
                            {prof.name}{prof.is_default ? " ★" : ""}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 truncate text-[11px] text-muted-foreground" title={effectiveProfile?.name || "Nema profila!"}>
                        Vlasnik: {effectiveProfile ? (
                          <span className="font-semibold text-foreground">{effectiveProfile.private_landlord_name || effectiveProfile.name}</span>
                        ) : (
                          <em className="text-destructive">Nema profila!</em>
                        )}
                        {!currentProfile && defaultProfile && (
                          <span className="ml-1 rounded bg-muted px-1.5 py-0.5">zadani</span>
                        )}
                      </p>
                    </div>
                    {/* Per-property trade name */}
                    <div className="min-w-0 flex-1 basis-1/3">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Trgovački naziv na računu
                        <span className="ml-1 text-muted-foreground/70" title="Ako je prazno, koristi se ime iznajmljivača iz profila.">?</span>
                      </label>
                      <input
                        type="text"
                        defaultValue={p.invoice_display_name || ""}
                        onBlur={(e) => {
                          if ((e.target.value || "") !== (p.invoice_display_name || "")) {
                            assignPropertyDisplayName(p.id, e.target.value);
                          }
                        }}
                        placeholder="npr. Apartmani Sunce"
                        className="rw-input w-full"
                        data-testid={`assign-display-name-${p.id}`}
                      />
                    </div>
                  </div>
                  <PropertyNumbering property={p} onSaved={load} />
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>)}
    </section>
  );
}

function ProfileEditor({ form, setForm, onSave, onCancel, saving, isNew }) {
  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : (e?.target?.value ?? e);
    setForm((f) => ({ ...f, [k]: v }));
  };
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onCancel} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-base font-semibold">{isNew ? "Novi profil za račune" : "Uredi profil"}</h4>
          <button onClick={onCancel} className="rounded-md p-1 hover:bg-muted"><X size={16} /></button>
        </div>

        <form onSubmit={onSave} className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          {/* Issuer type toggle */}
          <div className="rounded-md border border-border bg-muted/20 p-3">
            <p className="mb-2 text-xs font-medium text-foreground">Tip izdavatelja</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, issuer_type: "private" }))}
                className={`rounded-md border px-3 py-2 text-sm font-semibold ${form.issuer_type === "private" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
                data-testid="issuer-private"
              >
                Privatni iznajmljivač
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, issuer_type: "company" }))}
                className={`rounded-md border px-3 py-2 text-sm font-semibold ${form.issuer_type === "company" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
                data-testid="issuer-company"
              >
                Pravna osoba (firma / obrt)
              </button>
            </div>
            {form.issuer_type === "company" && (
              <p className="mt-2 text-[11px] text-muted-foreground">Na računu se dodaje zasebna stavka boravišne pristojbe (iz sezona u tabu „Boravišna pristojba”).</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Interni naziv profila *" hint="Npr. 'Mauro paušal' — vidljivo samo tebi">
              <input required value={form.name} onChange={set("name")} className="rw-input" data-testid="profile-form-name" />
            </Field>
            {form.issuer_type === "company" ? (
              <Field label="Naziv tvrtke *" hint="Puni naziv firme ili obrta za zaglavlje računa">
                <input required value={form.company_name || ""} onChange={set("company_name")} className="rw-input" data-testid="profile-form-company-name" />
              </Field>
            ) : (
              <Field label="Ime i prezime iznajmljivača *" hint="Ide u zaglavlje računa kao 'PRIVATNI IZNAJMLJIVAČ'">
                <input required value={form.private_landlord_name || ""} onChange={set("private_landlord_name")} className="rw-input" data-testid="profile-form-landlord" />
              </Field>
            )}
            <Field label="OIB / porezni broj *">
              <input value={form.oib || ""} onChange={set("oib")} maxLength={11} className="rw-input mono" data-testid="profile-form-oib" />
            </Field>
            <Field label="TAX / VAT ID" hint="Npr. 'HR48918530199' — za međunarodne račune">
              <input value={form.vat_id || ""} onChange={set("vat_id")} className="rw-input mono" data-testid="profile-form-vat-id" />
            </Field>
            <Field label="Model plaćanja" hint="Najčešće HR99">
              <input value={form.payment_model || ""} onChange={set("payment_model")} maxLength={4} className="rw-input mono" data-testid="profile-form-model" />
            </Field>
            <Field label="Poziv na broj" hint="{broj} = redni broj, {uredaj} = naplatni uređaj, {prostor} = znamenke oznake prostora, {godina}. Slova se uklanjaju.">
              <input value={form.reference_template || ""} onChange={set("reference_template")} className="rw-input mono" data-testid="profile-form-reference" />
            </Field>
            <Field label="Adresa">
              <input value={form.address || ""} onChange={set("address")} className="rw-input" data-testid="profile-form-address" />
            </Field>
            <Field label="Grad">
              <input value={form.city || ""} onChange={set("city")} className="rw-input" />
            </Field>
            <Field label="Poštanski broj">
              <input value={form.postal_code || ""} onChange={set("postal_code")} className="rw-input" />
            </Field>
            <Field label="Država">
              <input value={form.country || "HR"} onChange={set("country")} maxLength={2} className="rw-input mono" />
            </Field>
            <Field label="Telefon">
              <input value={form.phone || ""} onChange={set("phone")} className="rw-input" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email || ""} onChange={set("email")} className="rw-input" />
            </Field>
            <Field label="Web stranica">
              <input value={form.website || ""} onChange={set("website")} placeholder="vasadomena.com" className="rw-input" />
            </Field>
            <Field label="Račun izradio (osoba)" hint="Npr. 'Mauro Šokota' — pojavljuje se na dnu računa">
              <input value={form.issued_by_name || ""} onChange={set("issued_by_name")} className="rw-input" />
            </Field>
            <Field label="IBAN">
              <input value={form.iban || ""} onChange={set("iban")} className="rw-input mono" data-testid="profile-form-iban" />
            </Field>
            <Field label="SWIFT / BIC">
              <input value={form.swift || ""} onChange={set("swift")} className="rw-input mono" data-testid="profile-form-swift" />
            </Field>
            <Field label="Banka">
              <input value={form.bank_name || ""} onChange={set("bank_name")} className="rw-input" />
            </Field>
            <Field label="Logo URL (opcionalno)" hint="Direktni link na PNG/SVG logo, pojavljuje se u zaglavlju PDF-a">
              <input value={form.logo_url || ""} onChange={set("logo_url")} className="rw-input" />
            </Field>
          </div>

          <Field label="Tekst o PDV-u (obavezno)" hint="Ispisuje se u okviru na dnu svakog računa, npr. 'PDV nije obračunat temeljem čl. 90 st. 2 Zakona o PDV-u'">
            <textarea rows={2} required value={form.pdv_exemption_text || ""} onChange={set("pdv_exemption_text")} className="rw-input" data-testid="profile-form-pdv-text" />
          </Field>
          {form.issuer_type !== "company" && (
            <Field label="Tekst o boravišnoj pristojbi" hint="Dodaje se ispod teksta o PDV-u (paušalni iznajmljivači)">
              <textarea rows={2} value={form.tourist_tax_text || ""} onChange={set("tourist_tax_text")} className="rw-input" data-testid="profile-form-tourist-tax-text" />
            </Field>
          )}

          <Field label="Napomena za dno računa" hint="Opcionalno — pojaviti će se na svakom PDF računu">
            <textarea rows={2} value={form.footer_text || ""} onChange={set("footer_text")} className="rw-input" />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 rounded-md border border-border bg-muted/20 p-3 text-sm">
              <input type="checkbox" checked={form.vat_registered} onChange={set("vat_registered")} data-testid="profile-form-vat-registered" />
              U sustavu PDV-a
            </label>
            {form.vat_registered && (
              <Field label="Stopa PDV-a (%)">
                <input type="number" step="0.5" min={0} max={99} value={form.vat_rate} onChange={set("vat_rate")} className="rw-input" data-testid="profile-form-vat-rate" />
              </Field>
            )}
          </div>

          <label className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
            <input type="checkbox" checked={form.is_default} onChange={set("is_default")} data-testid="profile-form-default" />
            Postavi kao zadani profil (koristi se za objekte bez vlastite dodjele)
          </label>

          <div className="sticky bottom-0 -mx-1 flex justify-end gap-2 border-t border-border bg-card px-1 pt-4">
            <button type="button" onClick={onCancel} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted">Odustani</button>
            <button type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="profile-form-save">
              {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="duotone" />}
              Spremi
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}
